/**
 * Pure helpers for the <TenantSwitcher /> component (Task 16 / #49).
 *
 * Kept in their own module so they can be unit-tested in a `node`
 * environment without pulling in React, Vite, or the Amplify SDK. The
 * component file (`TenantSwitcher.jsx`) imports from here.
 */

/**
 * Should the switcher render at all?
 *
 * Rules:
 *   - User must have 2+ memberships (no point switching with only one).
 *   - When emulating another user, the switcher is hidden — emulation pins
 *     the actor to the emulated user's tenant context and a switch would
 *     desync the two.
 *
 * @param {Array} memberships
 * @param {boolean} isEmulating
 * @returns {boolean}
 */
export function shouldRenderSwitcher(memberships, isEmulating) {
  if (isEmulating) return false
  if (!Array.isArray(memberships)) return false
  return memberships.length > 1
}

/**
 * Format a membership entry as a display label, e.g. "Acme · Admin".
 *
 * Falls back gracefully if the tenant name or role is missing — the
 * backend `/me/memberships` shape is `{ tenantId, tenantName, role, ... }`
 * but we want the UI to still produce a sensible string under bad data.
 *
 * @param {{tenantId?: string, tenantName?: string, role?: string}} membership
 * @returns {string}
 */
export function formatTenantLabel(membership) {
  if (!membership) return ''
  const name = membership.tenantName || membership.tenantId || ''
  const role = membership.role || ''
  if (name && role) return `${name} · ${role}`
  return name || role || ''
}

/**
 * localStorage keys that hold tenant-scoped data and would leak across
 * tenants if not cleared on switch.
 *
 * KEEP THIS LIST UPDATED when adding new tenant-scoped client-side state.
 *
 * Excluded on purpose:
 *   - `preferredLanguage` — a per-user preference, not tenant-scoped.
 *   - `pendingPlan` — pre-auth signup intent, not tenant-scoped.
 *   - `e2e_auth_user` / `e2e_auth_token` — E2E-only stubs; tenant switching
 *     is not part of those flows.
 */
const TENANT_SCOPED_LOCAL_STORAGE_KEYS = [
  'inProgressImportId',
]

/**
 * Clear every localStorage key that holds tenant-scoped data. Called by
 * <TenantSwitcher /> right before navigating to `/` so the next tenant's
 * page-load doesn't see stale IDs (e.g. an in-progress import owned by the
 * previously-active tenant).
 *
 * Errors during `removeItem` (e.g. SecurityError in a sandboxed test
 * environment) are swallowed — failing to clear should never block the
 * user from completing the switch.
 */
export function clearTenantScopedStorage() {
  for (const key of TENANT_SCOPED_LOCAL_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key)
    } catch {
      // intentionally ignored — clearing is best-effort
    }
  }
}

// Exported for tests; intentionally not part of the public API.
export const __TENANT_SCOPED_LOCAL_STORAGE_KEYS = TENANT_SCOPED_LOCAL_STORAGE_KEYS
